import { Component, inject, OnInit } from '@angular/core';
import { PageHeaderComponent } from '../../../../../../shared/components/common/page-header/page-header.component';
import { Card } from '../../../../../../shared/components/common/card/card';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorService } from '../../../../../../shared/services/error.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import { DireccionesService } from '../direcciones.service';
import { LabelComponent } from '../../../../../../shared/components/form/label/label.component';
import { CreationButtons } from '../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import { EditionButtons } from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import { Spinner } from '../../../../../../shared/components/ui/spinner/spinner';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LocationCoordinates } from '@shared/components/form/location-picker/location-picker.component';
import { LocationPickerTriggerComponent } from '@shared/components/form/location-picker/location-picker-trigger.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { coordinateValidator } from '@shared/validators/coordinate.validator';

@Component({
  selector: 'app-crear-direccion',
  imports: [
    PageHeaderComponent,
    Card,
    ReactiveFormsModule,
    LabelComponent,
    CreationButtons,
    EditionButtons,
    Spinner,
    InputFieldComponent,
    LocationPickerTriggerComponent,
    AppFormComponent,
  ],
  templateUrl: './crear-direccion.html',
  styles: ``
})
export class CrearDireccion implements OnInit {
  saveForm!: FormGroup;
  loading = false;
  loadingData = false;
  isEditMode = false;
  direccionId: number | null = null;

  private fB = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private direccionesService = inject(DireccionesService);

  ngOnInit(): void {
    this.checkEditMode();
    this.makeForm();
  }

  private checkEditMode(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.isEditMode = true;
      this.direccionId = parseInt(id, 10);
      this.loadDireccion();
    }
  }

  private loadDireccion(): void {
    if (!this.direccionId) return;

    this.loadingData = true;
    this.direccionesService.show(this.direccionId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          const direccion = response.data;
          this.saveForm.patchValue({
            direccion: direccion.direccion,
            latitud: direccion.latitud,
            longitud: direccion.longitud,
          });
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
        this.goBack();
      }
    }).add(() => {
      this.loadingData = false;
    });
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      direccion: ['', [Validators.required, Validators.minLength(3)]],
      latitud: ['', [Validators.required, coordinateValidator(-90, 90)]],
      longitud: ['', [Validators.required, coordinateValidator(-180, 180)]],
    });
  }

  onCoordinatesChange(coordinates: LocationCoordinates): void {
    this.saveForm.patchValue(coordinates);
    this.saveForm.get('latitud')?.markAsDirty();
    this.saveForm.get('longitud')?.markAsDirty();
  }

  saveDireccion(): void {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const payload = {
      direccion: this.saveForm.value.direccion,
      latitud: this.saveForm.value.latitud,
      longitud: this.saveForm.value.longitud,
    };

    const request = this.isEditMode && this.direccionId
      ? this.direccionesService.update(this.direccionId, payload)
      : this.direccionesService.store(payload);

    request.subscribe({
      next: (response) => {
        if (response.status) {
          const message = this.isEditMode
            ? 'Dirección actualizada correctamente'
            : 'Dirección creada correctamente';
          this.snackbarService.showSnackBar(message);
          this.goBack();
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }
}
